import React, { useState, useCallback } from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useP9310Query } from './hooks/useP9310Query';
import { useP9310Lookup } from './hooks/useP9310Lookup';
import FilterPanel from './components/FilterPanel';
import ResultTable from './components/ResultTable';
import './P9310Form.css';

const P9310Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = useP9310Query();
  const { empFromName, empToName, onEmpFromBlur, onEmpToBlur, resetNames } = useP9310Lookup();

  // Filter state
  const [empFrom, setEmpFrom] = useState('');
  const [empTo, setEmpTo] = useState('');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());
  const [gradeFrom, setGradeFrom] = useState('');
  const [gradeTo, setGradeTo] = useState('');

  const handleQuery = useCallback(() => {
    runQuery({
      empFrom: empFrom.trim() || undefined,
      empTo: empTo.trim() || undefined,
      dateFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
      dateTo: dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
      gradeFrom: gradeFrom.trim() || undefined,
      gradeTo: gradeTo.trim() || undefined,
    });
  }, [empFrom, empTo, dateFrom, dateTo, gradeFrom, gradeTo, runQuery]);

  const handleReset = useCallback(() => {
    setEmpFrom('');
    setEmpTo('');
    setDateFrom(null);
    setDateTo(dayjs());
    setGradeFrom('');
    setGradeTo('');
    resetNames();
    reset();
  }, [reset, resetNames]);

  const handlePrint = useCallback(() => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>個人日出勤明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}
h2{text-align:center}table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}
.right{text-align:right}.center{text-align:center}
@media print{body{margin:8mm}}</style></head><body>
<h2>個人日出勤明細表</h2>
<table><thead><tr>
<th>員工代號</th><th>姓名</th><th>出勤日期</th><th>狀態碼</th><th>類別碼</th>
<th>打卡時間</th><th>班別</th><th>正常工時</th><th>加班時數</th>
<th>遲到(分)</th><th>早退(分)</th><th>職等</th><th>備註</th>
</tr></thead><tbody>
${data.map(r => {
  const punchTime = r.f910_13?.trim();
  const displayTime = punchTime && punchTime.length >= 4
    ? punchTime.substring(0, 2) + ':' + punchTime.substring(2, 4)
    : punchTime || '';
  return `<tr>
<td>${r.f910_01}</td><td>${r.emp_name || ''}</td><td>${(r.f910_02 || '').substring(0, 10)}</td>
<td class="center">${r.f910_11}</td><td class="center">${r.f910_12}</td>
<td>${displayTime}</td><td class="center">${r.f910_15}</td>
<td class="right">${r.f910_27}</td><td class="right">${r.f910_28}</td>
<td class="right">${r.f910_29}</td><td class="right">${r.f910_30}</td>
<td class="center">${r.f901_37}</td><td>${r.f910_14 || ''}</td></tr>`;
}).join('')}
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }, [data]);

  return (
    <PermissionGuard module={Modules.PW9310} action="query">
      <div className="p9310-container">
        <div className="p9310-header">
          <h2>個人日出勤明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <FilterPanel
          empFrom={empFrom}
          empTo={empTo}
          dateFrom={dateFrom}
          dateTo={dateTo}
          gradeFrom={gradeFrom}
          gradeTo={gradeTo}
          empFromName={empFromName}
          empToName={empToName}
          loading={loading}
          onEmpFromChange={setEmpFrom}
          onEmpToChange={setEmpTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onGradeFromChange={setGradeFrom}
          onGradeToChange={setGradeTo}
          onEmpFromBlur={() => onEmpFromBlur(empFrom)}
          onEmpToBlur={() => onEmpToBlur(empTo)}
          onQuery={handleQuery}
          onReset={handleReset}
        />

        <ResultTable
          data={data}
          loading={loading}
          queried={queried}
          noData={noData}
        />
      </div>
    </PermissionGuard>
  );
};

export default P9310Form;
